import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Breadcrumbs,
  Button,
  Card,
  ConfirmDialog,
  DropdownMenu,
  EmptyState,
  KpiTile,
  PageContainer,
  Spinner,
  Tabs,
  useToast,
} from '../../../ui/components';
import { IconButton } from '../../../ui/components/Button';
import Avatar from '../../../ui/components/Avatar';
import { Icon } from '../../../ui/icons';
import { useT } from '../../../i18n';
import { suppliersApi, statusMeta, SUPPLIER_STATUSES } from '../suppliersApi';
import { useProperty } from '../../../app/PropertyContext';
import { refFromSlug, supplierPath } from '../../../lib/slug';
import { toUpdatePayload } from './utils';
import OverviewTab from './OverviewTab';
import ProfileTab from './ProfileTab';
import ContactsTab from './ContactsTab';
import AddressesTab from './AddressesTab';
import BankAccountsTab from './BankAccountsTab';
import DocumentsTab from './DocumentsTab';
import NotesTab from './NotesTab';
import ActivityTab from './ActivityTab';
import SupplierCatalogTab from './SupplierCatalogTab';
import SupplierPurchasesTab from './SupplierPurchasesTab';
import SupplierSpendTab from './SupplierSpendTab';
import './profile.css';

const LOCKED_TABS = {};

export default function SupplierProfilePage() {
  const { id: routeRef } = useParams();
  const id = refFromSlug(routeRef);
  const navigate = useNavigate();
  const toast = useToast();
  const t = useT();
  const { hasPermission } = useProperty();
  const canManageCatalog = hasPermission('catalog.manage');
  const canViewPurchases = hasPermission('purchaseorders.view');

  const [supplier, setSupplier] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('overview');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Resolve the supplier from the (possibly short) slug ref, then key everything else off
      // its real id.
      const s = await suppliersApi.get(id);
      setSupplier(s);
      setStats(await suppliersApi.stats(s.id).catch(() => null));
    } catch (err) {
      setError(err?.message || t('Failed to load supplier.'));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    load();
  }, [load]);

  // Once loaded, rewrite the URL to the readable name slug.
  useEffect(() => {
    if (!supplier?.id) return;
    const canonical = supplierPath(supplier);
    if (`/suppliers/${routeRef}` !== canonical) {
      navigate(canonical, { replace: true });
    }
  }, [supplier, routeRef, navigate]);

  const refreshStats = useCallback(async () => {
    try {
      setStats(await suppliersApi.stats(supplier?.id || id));
    } catch {
      /* non-fatal */
    }
  }, [supplier?.id, id]);

  // Children that mutate the supplier (contacts/addresses/profile) pass the fresh DTO back.
  const onSupplierChanged = useCallback((updated) => {
    if (updated) setSupplier(updated);
    refreshStats();
  }, [refreshStats]);

  const changeStatus = async (newStatus) => {
    if (!supplier || newStatus === supplier.status) return;
    setStatusSaving(true);
    try {
      const res = await suppliersApi.update(supplier.id, toUpdatePayload(supplier, { status: newStatus }));
      setSupplier(res);
      refreshStats();
      toast.success(t('Status set to {status}', { status: t(statusMeta(newStatus).label) }));
    } catch (err) {
      toast.error(t('Could not change status'), { description: err?.message });
    } finally {
      setStatusSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await suppliersApi.remove(supplier?.id || id);
      toast.success(t('Supplier deleted'));
      navigate('/suppliers');
    } catch (err) {
      toast.error(t('Could not delete supplier'), { description: err?.message });
      setDeleting(false);
    }
  };

  const tabs = useMemo(() => ([
    { id: 'overview', label: t('Overview') },
    { id: 'profile', label: t('Profile') },
    { id: 'contacts', label: stats?.contactsCount ? t('Contacts ({count})', { count: stats.contactsCount }) : t('Contacts') },
    { id: 'addresses', label: stats?.addressesCount ? t('Addresses ({count})', { count: stats.addressesCount }) : t('Addresses') },
    { id: 'bank', label: supplier?.bankAccounts?.length ? t('Bank accounts ({count})', { count: supplier.bankAccounts.length }) : t('Bank accounts') },
    { id: 'documents', label: stats?.documentsCount ? t('Documents ({count})', { count: stats.documentsCount }) : t('Documents') },
    { id: 'notes', label: stats?.notesCount ? t('Notes ({count})', { count: stats.notesCount }) : t('Notes') },
    { id: 'activity', label: t('Activity') },
    { id: 'catalog', label: t('Catalog') },
    {
      id: 'purchases',
      label: canViewPurchases
        ? t('Purchases')
        : <span className="sp-tab-locked">{t('Purchases')} <Icon.Lock size={12} /></span>,
    },
    {
      id: 'spend',
      label: canViewPurchases
        ? t('Spend')
        : <span className="sp-tab-locked">{t('Spend')} <Icon.Lock size={12} /></span>,
    },
  ]), [stats, canViewPurchases, supplier?.bankAccounts?.length, t]);

  if (loading) {
    return (
      <PageContainer size="full">
        <div className="sp-center"><Spinner size={26} /></div>
      </PageContainer>
    );
  }

  if (error || !supplier) {
    return (
      <PageContainer size="full">
        <EmptyState
          icon={<Icon.CircleAlert size={28} />}
          title={t("Couldn't load supplier")}
          description={error || t('This supplier may have been removed.')}
          action={<Button variant="secondary" onClick={() => navigate('/suppliers')}>{t('Back to suppliers')}</Button>}
        />
      </PageContainer>
    );
  }

  const sm = statusMeta(supplier.status);

  return (
    <PageContainer size="full">
      <div className="sp-profile">
        <Breadcrumbs
          items={[
            { label: t('Suppliers'), onClick: () => navigate('/suppliers') },
            { label: supplier.name },
          ]}
        />

        <Card>
          <Card.Body>
            <div className="sp-header">
              <div className="sp-header__identity">
                <Avatar name={supplier.name} size="xl" shape="rounded" />
                <div className="sp-header__titles">
                  <div className="sp-header__name-row">
                    <h1 className="sp-header__name">{supplier.name}</h1>
                    <Badge tone={sm.tone} dot>{t(sm.label)}</Badge>
                    {supplier.isPreferred && (
                      <Badge tone="brand"><Icon.Star size={12} /> {t('Preferred')}</Badge>
                    )}
                  </div>
                  <div className="sp-header__sub">
                    {supplier.code && <span>{supplier.code}</span>}
                    {supplier.category && <span>· {supplier.category}</span>}
                    {supplier.email && (
                      <span><Icon.Mail size={13} /> {supplier.email}</span>
                    )}
                    {supplier.phone && (
                      <span><Icon.Phone size={13} /> {supplier.phone}</span>
                    )}
                  </div>
                  {supplier.tags?.length > 0 && (
                    <div className="sp-tags" style={{ marginTop: 'var(--space-2)' }}>
                      {supplier.tags.map((tag) => <Badge key={tag} tone="neutral">{tag}</Badge>)}
                    </div>
                  )}
                </div>
              </div>

              <div className="sp-header__actions">
                <DropdownMenu
                  trigger={
                    <Button variant="secondary" trailingIcon={<Icon.ChevronDown size={15} />} loading={statusSaving}>
                      {t('Status: {status}', { status: t(sm.label) })}
                    </Button>
                  }
                  items={SUPPLIER_STATUSES.map((s) => ({
                    id: s.value,
                    label: t(s.label),
                    icon: s.value === supplier.status ? <Icon.Check size={15} /> : undefined,
                    onSelect: () => changeStatus(s.value),
                  }))}
                />
                <Button variant="primary" leadingIcon={<Icon.Pencil size={15} />} onClick={() => setTab('profile')}>
                  {t('Edit profile')}
                </Button>
                <IconButton aria-label={t('Delete supplier')} variant="ghost" onClick={() => setDeleteOpen(true)}>
                  <Icon.Trash size={16} />
                </IconButton>
              </div>
            </div>
          </Card.Body>
        </Card>

        <div className="sp-kpis">
          <KpiTile label={t('Profile completeness')} value={`${stats?.profileCompleteness ?? 0}%`} />
          <KpiTile label={t('Contacts')} value={stats?.contactsCount ?? 0} />
          <KpiTile
            label={t('Documents')}
            value={stats?.documentsCount ?? 0}
            meta={stats?.expiringDocumentsCount ? t('{count} expiring', { count: stats.expiringDocumentsCount }) : undefined}
          />
          <KpiTile label={t('Notes')} value={stats?.notesCount ?? 0} />
          <KpiTile label={t('Activity events')} value={stats?.activityCount ?? 0} />
          <KpiTile label={t('Days as supplier')} value={stats?.daysAsSupplier ?? 0} />
        </div>

        <div className="sp-tabbar">
          <Tabs tabs={tabs} value={tab} onChange={setTab} ariaLabel={t('Supplier sections')} />
        </div>

        {tab === 'overview' && <OverviewTab supplier={supplier} stats={stats} onNavigateTab={setTab} />}
        {tab === 'profile' && <ProfileTab supplier={supplier} onSaved={onSupplierChanged} />}
        {tab === 'contacts' && <ContactsTab supplier={supplier} onChanged={onSupplierChanged} />}
        {tab === 'addresses' && <AddressesTab supplier={supplier} onChanged={onSupplierChanged} />}
        {tab === 'bank' && <BankAccountsTab supplier={supplier} onChanged={onSupplierChanged} />}
        {tab === 'documents' && <DocumentsTab supplierId={supplier.id} onChanged={refreshStats} />}
        {tab === 'notes' && <NotesTab supplierId={supplier.id} onChanged={refreshStats} />}
        {tab === 'activity' && <ActivityTab supplierId={supplier.id} />}
        {tab === 'catalog' && (
          <SupplierCatalogTab
            supplierId={supplier.id}
            defaultCurrency={supplier.currency || 'USD'}
            canManage={canManageCatalog}
            onChanged={refreshStats}
          />
        )}
        {tab === 'purchases' && (
          canViewPurchases
            ? <SupplierPurchasesTab supplierId={supplier.id} />
            : <EmptyState icon={<Icon.Lock size={28} />} title={t('No access')} description={t("You don't have permission to view purchase orders.")} />
        )}
        {tab === 'spend' && (
          canViewPurchases
            ? <SupplierSpendTab supplierId={supplier.id} />
            : <EmptyState icon={<Icon.Lock size={28} />} title={t('No access')} description={t("You don't have permission to view purchase orders.")} />
        )}
        {LOCKED_TABS[tab] && (
          <EmptyState
            icon={<Icon.Lock size={28} />}
            title={t('Coming soon')}
            description={LOCKED_TABS[tab]}
          />
        )}
      </div>

      <ConfirmDialog
        open={deleteOpen}
        onCancel={() => (deleting ? null : setDeleteOpen(false))}
        onConfirm={handleDelete}
        title={t('Delete supplier')}
        description={t('Delete "{name}"? This also removes its contacts, documents and notes. This cannot be undone.', { name: supplier.name })}
        confirmLabel={t('Delete')}
        tone="danger"
        loading={deleting}
      />
    </PageContainer>
  );
}
